import { useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Button } from "./components/ui/button";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import VCardPage from "./pages/VCardPage";
import {
  Download,
  Copy,
  CreditCard,
  QrCode,
  Share2,
  Phone,
  Mail,
  Globe,
  Facebook,
  Instagram,
  Leaf,
  FileText,
  Printer,
  Shield,
  Megaphone,
  Home as HomeIcon,
  Heart,
  Users,
  CheckCircle
} from
"lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_citizen-restart-fund/artifacts/35d9mfhq_6zh9nv37_transparent%20background.png";
const COMMUNITY_IMAGE = "https://images.unsplash.com/photo-1761586449543-edbcad36276e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxwZW9wbGUlMjBob2xkaW5nJTIwaGFuZHMlMjB0b2dldGhlciUyMGNvbW11bml0eSUyMHN1cHBvcnR8ZW58MHx8fHwxNzc1MDU3MDM3fDA&ixlib=rb-4.1.0&q=85";
const TEXTURE_BG = "https://images.pexels.com/photos/7599590/pexels-photo-7599590.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Home() {
  const [cardData, setCardData] = useState({
    name: "Diane Slappy",
    title: "BSN, RN",
    organization: "New Leaf Independent Housing",
    tagline: "Rooted, Connected & Growing in Purpose",
    phone: "(912) 698-1099",
    email: "info@newleaf-housing.com",
    website: "newleafhousing.info",
    facebook: "NewLeafIndependentHousing",
    instagram: "newleaf_independent_housing"
  });

  const [fbPost, setFbPost] = useState(`ANNOUNCEMENT: Housing Solutions for Your Clients

Calling all case managers, social workers, and discharge planners!

Are you looking for safe, supportive housing for individuals? Allow New Leaf Independent Housing to be the solution.

ROOTED, CONNECTED AND GROWING FOR COMMUNITY

We provide a stable environment designed to help your clients thrive. Our services are tailored to meet the needs of:

• Veterans
• Seniors
• Homeless / Displaced Individuals
• Returning Citizens
• DV Survivors
• Individuals on a Fixed Income

Let us help you find the right housing solution for those you serve.

Visit our website to learn more: newleafhousing.info`);

  const qrRef = useRef(null);
  const cardRef = useRef(null);

  const handleInputChange = (field, value) => {
    setCardData((prev) => ({ ...prev, [field]: value }));
  };

  // Generate vCard string
  const generateVCard = () => {
    return `BEGIN:VCARD
VERSION:3.0
FN:${cardData.name}
ORG:${cardData.organization}
TITLE:${cardData.title}
TEL;TYPE=WORK,VOICE:${cardData.phone}
EMAIL:${cardData.email}
URL:https://${cardData.website}
END:VCARD`;
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 400, 400);

      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = "newleaf-qrcode.png";
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    toast.success("QR Code downloaded!");
  };

  const copyFbPost = async () => {
    try {
      await navigator.clipboard.writeText(fbPost);
      toast.success("Facebook post copied to clipboard!");
    } catch (err) {
      // Fallback for browsers without clipboard API permissions
      const textArea = document.createElement("textarea");
      textArea.value = fbPost;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Facebook post copied to clipboard!");
      } catch (execErr) {
        toast.error("Unable to copy. Please select and copy manually.");
      }
      textArea.remove();
    }
  };

  const sharePost = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(fbPost)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-[#F9F8F5]">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-50 bg-white border-b border-[#D5D8D2] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="New Leaf Housing Logo"
              className="h-12 w-auto"
              data-testid="header-logo" />

            <div>
              <h1 className="text-xl font-bold text-[#1A2E20] font-['Manrope']">New Leaf Tools</h1>
              <p className="text-sm text-[#6C7D71]">Business Card & Marketing Suite</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="social-flyer" className="w-full">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-6 mb-8 bg-[#EBEAE4] p-1 rounded-xl">
            <TabsTrigger
              value="social-flyer"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-social-flyer">
              <Megaphone className="w-4 h-4" />
              <span className="hidden sm:inline">Social</span>
            </TabsTrigger>
            <TabsTrigger
              value="flyer"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-flyer">

              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Flyer</span>
            </TabsTrigger>
            <TabsTrigger
              value="veterans-flyer"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-veterans-flyer">

              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Veterans</span>
            </TabsTrigger>
            <TabsTrigger
              value="business-card"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-business-card">

              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Card</span>
            </TabsTrigger>
            <TabsTrigger
              value="qr-code"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-qr-code">

              <QrCode className="w-4 h-4" />
              <span className="hidden sm:inline">QR</span>
            </TabsTrigger>
            <TabsTrigger
              value="facebook"
              className="flex items-center gap-2 data-[state=active]:bg-[#8CA891] data-[state=active]:text-[#1A2E20] rounded-lg font-semibold"
              data-testid="tab-facebook">

              <Facebook className="w-4 h-4" />
              <span className="hidden sm:inline">Post</span>
            </TabsTrigger>
          </TabsList>

          {/* Social Media Flyer Tab */}
          <TabsContent value="social-flyer" data-testid="content-social-flyer">
            <div className="flex flex-col items-center gap-6">
              <div className="flex gap-4">
                <Button
                  onClick={() => window.print()}
                  className="bg-[#C57E65] hover:bg-[#B36B52] text-white rounded-xl py-3 px-6 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                  data-testid="print-social-flyer-button">
                  <Printer className="w-4 h-4 mr-2" />
                  Download / Print
                </Button>
              </div>
              
              {/* Social Media Flyer Preview - Square format for social */}
              <div 
                className="relative border-4 border-[#265C39] rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden print:border-0 print:shadow-none"
                data-testid="social-flyer-preview"
                id="social-flyer-content"
              >
                {/* Bold Header */}
                <div className="bg-[#C57E65] text-white p-4 text-center">
                  <p className="text-sm font-bold tracking-widest uppercase">🚨 URGENT COMMUNITY MESSAGE 🚨</p>
                </div>

                {/* Main Content Area */}
                <div className="bg-[#265C39] text-white p-8">
                  {/* Main Headline */}
                  <div className="text-center mb-6">
                    <h1 className="text-4xl sm:text-5xl font-black font-['Manrope'] leading-tight mb-2">
                      STOP HOMELESSNESS
                    </h1>
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#C57E65]">
                      IN HINESVILLE, GEORGIA
                    </h2>
                  </div>

                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    <img src={LOGO_URL} alt="New Leaf Housing Logo" className="h-16 w-auto" />
                  </div>

                  {/* Tagline */}
                  <p className="text-center text-[#8CA891] text-lg italic mb-6 font-semibold">
                    ROOTED, CONNECTED & GROWING FOR COMMUNITY
                  </p>

                  {/* Main Message */}
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-6">
                    <p className="text-center text-xl font-bold">
                      We provide STABLE and SAFE housing
                    </p>
                  </div>

                  {/* Services List */}
                  <div className="mb-6">
                    <p className="text-center text-lg font-semibold mb-4 text-[#8CA891]">
                      Our services are tailored to meet the needs of:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { icon: '✅', text: 'Veterans' },
                        { icon: '✅', text: 'Seniors' },
                        { icon: '✅', text: 'Homeless / Displaced' },
                        { icon: '✅', text: 'Returning Citizens' },
                        { icon: '✅', text: 'DV Survivors' },
                        { icon: '✅', text: 'Fixed Income' }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white/10 p-3 rounded-lg">
                          <span className="text-xl">{item.icon}</span>
                          <span className="font-bold text-sm">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Housing Offer */}
                  <div className="bg-[#C57E65] rounded-xl p-4 mb-6 text-center">
                    <p className="text-xl font-bold flex items-center justify-center gap-2">
                      <span className="text-2xl">🏡</span>
                      Private & Shared Rooms Available
                    </p>
                    <p className="text-sm mt-1 opacity-90">
                      Safe, supportive, and hopeful spaces for new beginnings
                    </p>
                  </div>

                  {/* Contact Info */}
                  <div className="border-t-2 border-white/30 pt-4">
                    <p className="text-center font-bold text-lg mb-3">CONTACT US TODAY:</p>
                    <div className="flex flex-wrap justify-center gap-4 text-sm">
                      <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                        <Phone className="w-4 h-4" />
                        <span className="font-bold">{cardData.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                        <Globe className="w-4 h-4" />
                        <span className="font-bold">{cardData.website}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-[#1A2E20] text-white p-3 text-center">
                  <p className="text-sm font-bold">
                    NEW LEAF INDEPENDENT HOUSING • HINESVILLE, GA
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Flyer Tab */}
          <TabsContent value="flyer" data-testid="content-flyer">
            <div className="flex flex-col items-center gap-6">
              <div className="flex gap-4">
                <Button
                  onClick={() => window.print()}
                  className="bg-[#265C39] hover:bg-[#1E472C] text-white rounded-xl py-3 px-6 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                  data-testid="print-flyer-button">

                  <Printer className="w-4 h-4 mr-2" />
                  Print Flyer
                </Button>
              </div>
              
              {/* Flyer Preview */}
              <div
                className="relative border-2 border-[#D5D8D2] rounded-lg shadow-2xl w-full max-w-2xl print:border-0 print:shadow-none print:max-w-none overflow-hidden"
                data-testid="flyer-preview"
                id="flyer-content"
                style={{
                  backgroundImage: `url(${TEXTURE_BG})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>

                <div className="relative z-10">
                {/* Header Banner */}
                <div className="bg-[#265C39]/95 text-white p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <img src={LOGO_URL} alt="New Leaf Housing Logo" className="h-20 w-auto" />
                  </div>
                  <h1 className="text-3xl font-bold font-['Manrope'] mb-2">New Leaf Independent Housing</h1>
                  <p className="text-[#8CA891] text-lg italic">Rooted, Connected & Growing for Community</p>
                </div>

                {/* Main Content */}
                <div className="p-8">
                  {/* Announcement Header */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-[#C57E65] font-['Manrope'] mb-2">
                      ANNOUNCEMENT
                    </h2>
                    <h3 className="text-xl font-semibold text-[#1A2E20]">
                      Housing Solutions for Your Clients
                    </h3>
                  </div>

                  {/* Call to Action */}
                  <div className="bg-[#F9F8F5] border-l-4 border-[#265C39] p-4 mb-6 rounded-r-lg">
                    <p className="!font-bold !text-lg text-[#1A2E20]">
                      Calling all case managers, social workers, and discharge planners!
                    </p>
                  </div>

                  <p className="text-[#3F4F44] text-lg mb-6 text-center">
                    Are you looking for safe, supportive housing for individuals?<br />
                    <span className="font-semibold text-[#265C39]">Allow New Leaf Independent Housing to be the solution.</span>
                  </p>

                  {/* Services List */}
                  <div className="mb-6">
                    <p className="text-[#1A2E20] font-semibold mb-4 text-center">
                      We provide a stable environment designed to help your clients thrive.<br />
                      Our services are tailored to meet the needs of:
                    </p>
                    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                      {['Veterans', 'Seniors', 'Homeless / Displaced Individuals', 'Returning Citizens', 'DV Survivors', 'Individuals on a Fixed Income'].map((service) =>
                        <div key={service} className="flex items-center gap-2 bg-[#EBEAE4] p-3 rounded-lg">
                          <Leaf className="w-5 h-5 text-[#265C39] flex-shrink-0" />
                          <span className="text-[#1A2E20] text-sm font-medium">{service}</span>
                        </div>
                        )}
                    </div>
                  </div>

                  {/* Contact Section */}
                  <div className="bg-[#265C39] text-white p-6 rounded-xl mt-6">
                    <p className="text-center text-lg mb-4 font-medium">
                      Let us help you find the right housing solution for those you serve.
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.website}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.email}</span>
                      </div>
                    </div>
                    <div className="flex justify-center gap-4 mt-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Facebook className="w-4 h-4 text-[#8CA891]" />
                        <span>@{cardData.facebook}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Instagram className="w-4 h-4 text-[#8CA891]" />
                        <span>@{cardData.instagram}</span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Veterans Flyer Tab */}
          <TabsContent value="veterans-flyer" data-testid="content-veterans-flyer">
            <div className="flex flex-col items-center gap-6">
              <div className="flex gap-4">
                <Button
                  onClick={() => window.print()}
                  className="bg-[#265C39] hover:bg-[#1E472C] text-white rounded-xl py-3 px-6 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                  data-testid="print-veterans-flyer-button">

                  <Printer className="w-4 h-4 mr-2" />
                  Print Veterans Flyer
                </Button>
              </div>
              
              {/* Veterans Flyer Preview */}
              <div
                className="relative border-2 border-[#D5D8D2] rounded-lg shadow-2xl w-full max-w-2xl print:border-0 print:shadow-none print:max-w-none overflow-hidden"
                data-testid="veterans-flyer-preview"
                id="veterans-flyer-content"
                style={{
                  backgroundImage: `url(${TEXTURE_BG})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>

                <div className="relative z-10">
                {/* Header Banner with American colors accent */}
                <div className="bg-[#265C39]/95 text-white p-6 text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-700 via-white to-red-600"></div>
                  <div className="flex justify-center mb-4">
                    <img src={LOGO_URL} alt="New Leaf Housing Logo" className="h-20 w-auto" />
                  </div>
                  <h1 className="text-3xl font-bold font-['Manrope'] mb-2">New Leaf Independent Housing</h1>
                  <p className="text-[#8CA891] text-lg italic">Honoring Those Who Served</p>
                </div>

                {/* Main Content */}
                <div className="p-8">
                  {/* Veterans Header */}
                  <div className="text-center mb-6">
                    <div className="flex justify-center mb-3">
                      <Shield className="w-12 h-12 text-[#265C39]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#C57E65] font-['Manrope'] mb-2">
                      ATTENTION VETERANS
                    </h2>
                    <h3 className="text-xl font-semibold text-[#1A2E20]">
                      You Served Our Country. Now Let Us Serve You.
                    </h3>
                  </div>

                  {/* Message */}
                  <div className="bg-[#265C39]/10 border-l-4 border-[#265C39] p-4 mb-6 rounded-r-lg">
                    <p className="text-[#1A2E20] font-medium text-lg">
                      Are you a veteran experiencing homelessness or in need of stable housing?
                    </p>
                  </div>

                  <p className="text-[#3F4F44] text-lg mb-6 text-center">
                    <span className="font-semibold text-[#265C39]">New Leaf Independent Housing</span> is here to help you find a safe, supportive place to call home.
                  </p>

                  {/* What We Offer */}
                  <div className="mb-6">
                    <p className="text-[#1A2E20] font-bold mb-4 text-center text-lg">
                      What We Offer:
                    </p>
                    <div className="grid grid-cols-1 gap-3 max-w-md mx-auto">
                      {[
                        'Safe & Stable Housing',
                        'Supportive Environment',
                        'Respect & Dignity',
                        'A Fresh Start'].
                        map((item) =>
                        <div key={item} className="flex items-center gap-3 bg-[#EBEAE4] p-3 rounded-lg">
                          <Leaf className="w-5 h-5 text-[#265C39] flex-shrink-0" />
                          <span className="text-[#1A2E20] font-medium">{item}</span>
                        </div>
                        )}
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="text-center mb-6 p-4 bg-[#C57E65]/10 rounded-lg">
                    <p className="text-[#1A2E20] font-semibold text-lg">
                      Your service matters. Your future matters.
                    </p>
                    <p className="text-[#3F4F44]">
                      Take the first step toward a new beginning.
                    </p>
                  </div>

                  {/* Contact Section */}
                  <div className="bg-[#265C39] text-white p-6 rounded-xl">
                    <p className="text-center text-lg mb-4 font-bold">
                      REACH OUT TODAY
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.website}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-[#8CA891]" />
                        <span>{cardData.email}</span>
                      </div>
                    </div>
                    <p className="text-center text-[#8CA891] mt-4 text-sm italic">
                      Rooted, Connected & Growing in Purpose
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Business Card Tab */}
          <TabsContent value="business-card" data-testid="content-business-card">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Form Section */}
              <div className="lg:col-span-4">
                <Card className="bg-white border-[#D5D8D2] shadow-[0_8px_32px_rgba(38,92,57,0.06)]">
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-xl font-bold text-[#1A2E20] font-['Manrope'] mb-4">Edit Business Card</h2>
                    
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#3F4F44] font-medium">Full Name</Label>
                      <Input
                        id="name"
                        data-testid="input-name"
                        value={cardData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title" className="text-[#3F4F44] font-medium">Title/Credentials</Label>
                      <Input
                        id="title"
                        data-testid="input-title"
                        value={cardData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="organization" className="text-[#3F4F44] font-medium">Organization</Label>
                      <Input
                        id="organization"
                        data-testid="input-organization"
                        value={cardData.organization}
                        onChange={(e) => handleInputChange('organization', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tagline" className="text-[#3F4F44] font-medium">Tagline</Label>
                      <Input
                        id="tagline"
                        data-testid="input-tagline"
                        value={cardData.tagline}
                        onChange={(e) => handleInputChange('tagline', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#3F4F44] font-medium">Phone</Label>
                      <Input
                        id="phone"
                        data-testid="input-phone"
                        value={cardData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#3F4F44] font-medium">Email</Label>
                      <Input
                        id="email"
                        data-testid="input-email"
                        value={cardData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-[#3F4F44] font-medium">Website</Label>
                      <Input
                        id="website"
                        data-testid="input-website"
                        value={cardData.website}
                        onChange={(e) => handleInputChange('website', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="facebook" className="text-[#3F4F44] font-medium">Facebook</Label>
                      <Input
                        id="facebook"
                        data-testid="input-facebook"
                        value={cardData.facebook}
                        onChange={(e) => handleInputChange('facebook', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="text-[#3F4F44] font-medium">Instagram</Label>
                      <Input
                        id="instagram"
                        data-testid="input-instagram"
                        value={cardData.instagram}
                        onChange={(e) => handleInputChange('instagram', e.target.value)}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891]" />

                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Preview Section */}
              <div className="lg:col-span-8 space-y-8" ref={cardRef}>
                <h2 className="text-xl font-bold text-[#1A2E20] font-['Manrope']">Card Preview</h2>
                
                {/* Front Card */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#6C7D71] uppercase tracking-wide">Front</p>
                  <div
                    className="business-card bg-white p-6 flex flex-col justify-between max-w-md"
                    data-testid="card-front-preview">

                    <div className="flex items-start justify-between">
                      <img src={LOGO_URL} alt="Logo" className="h-10 w-auto" />
                      <Leaf className="w-6 h-6 text-[#8CA891]" />
                    </div>
                    <div className="mt-auto">
                      <h3 className="text-2xl font-bold text-[#1A2E20] font-['Manrope']">{cardData.name}</h3>
                      <p className="text-[#C57E65] font-semibold">{cardData.title}</p>
                      <p className="text-[#3F4F44] text-sm">{cardData.organization}</p>
                      <p className="text-[#6C7D71] text-xs italic mt-1">{cardData.tagline}</p>
                    </div>
                  </div>
                </div>

                {/* Back Card */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#6C7D71] uppercase tracking-wide">Back</p>
                  <div
                    className="business-card bg-[#265C39] p-6 flex flex-col justify-between max-w-md"
                    data-testid="card-back-preview">

                    <div className="flex items-start justify-between">
                      <img src={LOGO_URL} alt="Logo" className="h-8 w-auto brightness-0 invert" />
                      <div className="text-right">
                        <p className="text-white font-bold text-lg font-['Manrope']">New Leaf</p>
                        <p className="text-[#8CA891] text-sm">Independent Housing</p>
                      </div>
                    </div>
                    <div className="mt-auto space-y-2">
                      <div className="flex items-center gap-2 text-white">
                        <Phone className="w-4 h-4 text-[#8CA891]" />
                        <span className="text-sm">{cardData.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white">
                        <Globe className="w-4 h-4 text-[#8CA891]" />
                        <span className="text-sm">{cardData.website}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white">
                        <Mail className="w-4 h-4 text-[#8CA891]" />
                        <span className="text-sm">{cardData.email}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1 text-white">
                          <Facebook className="w-4 h-4 text-[#8CA891]" />
                          <span className="text-xs">@{cardData.facebook}</span>
                        </div>
                        <div className="flex items-center gap-1 text-white">
                          <Instagram className="w-4 h-4 text-[#8CA891]" />
                          <span className="text-xs">@{cardData.instagram}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* QR Code Tab */}
          <TabsContent value="qr-code" data-testid="content-qr-code">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Info Section */}
              <div className="lg:col-span-4">
                <Card className="bg-white border-[#D5D8D2] shadow-[0_8px_32px_rgba(38,92,57,0.06)]">
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-xl font-bold text-[#1A2E20] font-['Manrope'] mb-4">vCard QR Code</h2>
                    <p className="text-[#3F4F44]">
                      This QR code contains your contact information in vCard format. When scanned, it allows people to instantly save your contact details to their phone.
                    </p>
                    <div className="bg-[#F9F8F5] p-4 rounded-lg border border-[#D5D8D2]">
                      <h3 className="font-semibold text-[#1A2E20] mb-2">Included Information:</h3>
                      <ul className="space-y-1 text-sm text-[#3F4F44]">
                        <li>• Name: {cardData.name}</li>
                        <li>• Title: {cardData.title}</li>
                        <li>• Organization: {cardData.organization}</li>
                        <li>• Phone: {cardData.phone}</li>
                        <li>• Email: {cardData.email}</li>
                        <li>• Website: {cardData.website}</li>
                      </ul>
                    </div>
                    <Button
                      onClick={downloadQR}
                      className="w-full bg-[#265C39] hover:bg-[#1E472C] text-white rounded-xl py-3 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                      data-testid="download-qr-button">

                      <Download className="w-4 h-4 mr-2" />
                      Download QR Code
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* QR Display */}
              <div className="lg:col-span-8 flex items-center justify-center">
                <div
                  ref={qrRef}
                  className="bg-white p-12 rounded-2xl shadow-[0_8px_32px_rgba(38,92,57,0.1)] border border-[#D5D8D2]"
                  data-testid="qr-code-display">

                  <QRCodeSVG
                    value={generateVCard()}
                    size={300}
                    level="H"
                    includeMargin={true}
                    fgColor="#265C39"
                    bgColor="#FFFFFF"
                    imageSettings={{
                      src: LOGO_URL,
                      x: undefined,
                      y: undefined,
                      height: 50,
                      width: 50,
                      excavate: true
                    }} />

                  <p className="text-center mt-4 text-[#6C7D71] text-sm">Scan to save contact</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Facebook Post Tab */}
          <TabsContent value="facebook" data-testid="content-facebook">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Editor Section */}
              <div className="lg:col-span-5">
                <Card className="bg-white border-[#D5D8D2] shadow-[0_8px_32px_rgba(38,92,57,0.06)]">
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-xl font-bold text-[#1A2E20] font-['Manrope'] mb-4">Facebook Post Editor</h2>
                    <p className="text-[#3F4F44] text-sm">
                      Create a compassionate post for veterans, homeless seniors, and returning citizens seeking housing.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="fb-post" className="text-[#3F4F44] font-medium">Post Content</Label>
                      <Textarea
                        id="fb-post"
                        data-testid="input-fb-post"
                        value={fbPost}
                        onChange={(e) => setFbPost(e.target.value)}
                        rows={12}
                        className="bg-[#F9F8F5] border-[#D5D8D2] focus:border-[#8CA891] focus:ring-[#8CA891] resize-none" />

                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={copyFbPost}
                        className="flex-1 bg-[#265C39] hover:bg-[#1E472C] text-white rounded-xl py-3 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                        data-testid="copy-fb-post-button">

                        <Copy className="w-4 h-4 mr-2" />
                        Copy Text
                      </Button>
                      <Button
                        onClick={sharePost}
                        className="flex-1 bg-[#C57E65] hover:bg-[#B36B52] text-white rounded-xl py-3 font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                        data-testid="share-fb-post-button">

                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Preview Section */}
              <div className="lg:col-span-7">
                <h3 className="text-lg font-semibold text-[#1A2E20] font-['Manrope'] mb-4">Preview</h3>
                <div className="fb-post-preview max-w-lg" data-testid="fb-post-preview">
                  {/* FB Header */}
                  <div className="fb-header border-b border-gray-100">
                    <img
                      src={LOGO_URL}
                      alt="Profile"
                      className="fb-avatar bg-[#265C39] p-1" />

                    <div>
                      <p className="font-semibold text-[#1A2E20]">New Leaf Independent Housing</p>
                      <p className="text-xs text-gray-500">Just now · Public</p>
                    </div>
                  </div>
                  
                  {/* FB Content */}
                  <div className="fb-content text-[#1A2E20]">
                    {fbPost}
                  </div>
                  
                  {/* FB Image */}
                  <img
                    src={COMMUNITY_IMAGE}
                    alt="Community gathering"
                    className="fb-image h-64" />

                  
                  {/* FB Actions */}
                  <div className="flex justify-around py-3 border-t border-gray-100 text-gray-500">
                    <button className="flex items-center gap-2 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                      </svg>
                      <span className="text-sm">Like</span>
                    </button>
                    <button className="flex items-center gap-2 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <span className="text-sm">Comment</span>
                    </button>
                    <button className="flex items-center gap-2 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                      <span className="text-sm">Share</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-[#265C39] text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#8CA891] text-sm">
            New Leaf Independent Housing · Rooted, Connected & Growing in Purpose
          </p>
        </div>
      </footer>
    </div>);

}

// Main App with routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/vcard" element={<VCardPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;